import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  ArrowRight,
  Sparkles,
  Building2,
  Mic,
  Zap,
  GraduationCap,
  KeyRound,
  Lock,
} from "lucide-react";
import { toast } from "sonner";

const ADMIN_PASSCODE = "310721";

/** The five products — one short card each. */
const PRODUCTS = [
  {
    key: "founder",
    name: "Operon Founder",
    tagline: "The full AI platform for therapeutic-startup founders.",
    icon: Sparkles,
    accent: "teal",
    path: "/auth",
    cta: "Sign in",
    status: "live",
  },
  {
    key: "campus",
    name: "Operon Campus",
    tagline: "Run your science park: tenants, events, resources, visitors.",
    icon: Building2,
    accent: "amber",
    path: "/campus",
    cta: "Learn more",
    status: "private-beta",
  },
  {
    key: "panels",
    name: "Operon Panels",
    tagline: "Run better panels — speakers, questions, audience, archive.",
    icon: Mic,
    accent: "violet",
    path: "/panels",
    cta: "Learn more",
    status: "private-beta",
  },
  {
    key: "sprint",
    name: "Operon Sprint",
    tagline: "A 3-hour diagnostic: what's going well, what's wrong, what next.",
    icon: Zap,
    accent: "emerald",
    path: "/sprint",
    cta: "Learn more",
    status: "private-beta",
  },
  {
    key: "accelerator",
    name: "Operon Accelerator",
    tagline: "Strategic programmes connecting startups to the UK ecosystem.",
    icon: GraduationCap,
    accent: "cyan",
    path: "/accelerator",
    cta: "Learn more",
    status: "live",
  },
];

const ACCENT_STYLES = {
  teal:    { border: "hover:border-teal-400/60",    icon: "text-teal-400",    bg: "from-teal-500/10 to-teal-500/0",       badge: "bg-teal-500/15 text-teal-300 border-teal-500/30" },
  amber:   { border: "hover:border-amber-400/60",   icon: "text-amber-400",   bg: "from-amber-500/10 to-amber-500/0",     badge: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  violet:  { border: "hover:border-violet-400/60",  icon: "text-violet-400",  bg: "from-violet-500/10 to-violet-500/0",   badge: "bg-violet-500/15 text-violet-300 border-violet-500/30" },
  emerald: { border: "hover:border-emerald-400/60", icon: "text-emerald-400", bg: "from-emerald-500/10 to-emerald-500/0", badge: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  cyan:    { border: "hover:border-cyan-400/60",    icon: "text-cyan-400",    bg: "from-cyan-500/10 to-cyan-500/0",       badge: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" },
};

export default function Home() {
  const navigate = useNavigate();
  const [showAdmin, setShowAdmin] = useState(false);
  const [passcode, setPasscode] = useState("");

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleAdminSubmit = (e) => {
    e.preventDefault();
    if (passcode === ADMIN_PASSCODE) {
      setShowAdmin(false);
      navigate("/admin-area");
    } else {
      toast.error("Incorrect passcode");
      setPasscode("");
    }
  };

  return (
    <div className="min-h-screen bg-[#060B1C] text-[#E9EEF9] font-sans overflow-x-hidden">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute top-[-10%] left-[-10%] w-[55rem] h-[55rem] rounded-full bg-[#3E7AD6]/10 blur-[140px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50rem] h-[50rem] rounded-full bg-[#57C7E6]/10 blur-[140px]" />
      </div>

      {/* Top bar with admin passcode */}
      <nav className="border-b border-[#243768]/50 bg-[#0B1735]/40 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/logo-complete.png" alt="Operon" className="h-10 object-contain" />
          <button
            onClick={() => setShowAdmin(true)}
            className="text-slate-400 hover:text-white p-2 rounded-full border border-transparent hover:border-[#243768] transition-colors"
            aria-label="Admin access"
            data-testid="btn-admin-access"
          >
            <KeyRound className="w-4 h-4" />
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-20 pb-14 md:pt-28 md:pb-20 text-center">
        <div className="inline-block text-[11px] tracking-[0.3em] font-mono text-cyan-300/80 uppercase mb-6 px-3 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/5">
          One brand · Five products · One promise
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight mb-6">
          Platforms with a <span className="text-cyan-300">person</span> in the loop.
        </h1>
        <p className="text-lg text-[#9DB0D6] leading-relaxed max-w-2xl mx-auto">
          For therapeutic-startup founders, investors, pharma, CROs — and the science parks, panel organisers, strategy consultants and accelerator leads who work with them.
        </p>
      </section>

      {/* 5 product tiles */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="product-tiles">
          {PRODUCTS.map((p) => {
            const a = ACCENT_STYLES[p.accent];
            const Icon = p.icon;
            return (
              <button
                key={p.key}
                onClick={() => navigate(p.path)}
                data-testid={`tile-${p.key}`}
                className={`group relative overflow-hidden rounded-2xl border border-[#243768] bg-gradient-to-br ${a.bg} bg-[#0B1735]/60 p-6 text-left transition-all ${a.border} hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#57C7E6]/5`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className={`w-12 h-12 rounded-xl border border-[#243768] bg-[#060B1C] flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${a.icon}`} />
                  </div>
                  {p.status === "private-beta" && (
                    <span className={`text-[9px] font-mono tracking-[0.2em] uppercase px-2 py-0.5 rounded-full border ${a.badge}`}>
                      Private beta
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{p.name}</h3>
                <p className="text-sm text-[#9DB0D6] leading-relaxed mb-5 min-h-[3rem]">{p.tagline}</p>
                <div className={`flex items-center gap-2 text-sm font-medium ${a.icon}`}>
                  {p.cta}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#243768]/50 py-10 mt-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-[10px] font-mono text-[#6C7EA6] tracking-[0.3em] uppercase">
            Activate the right pathways
          </div>
          <div className="text-xs text-[#6C7EA6]">
            © {new Date().getFullYear()} Operon · <a href="mailto:marco@operon-hq.com" className="hover:text-cyan-300">marco@operon-hq.com</a>
          </div>
        </div>
      </footer>

      {/* Admin passcode dialog */}
      <Dialog open={showAdmin} onOpenChange={(o) => { setShowAdmin(o); if (!o) setPasscode(""); }}>
        <DialogContent className="bg-[#0B1735] border-[#243768] text-[#E9EEF9] max-w-sm" data-testid="admin-passcode-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Lock className="w-5 h-5 text-cyan-400" />
              Admin access
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdminSubmit} className="space-y-4 mt-2">
            <Input
              type="password"
              placeholder="6-digit passcode"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              autoFocus
              className="bg-[#0F1D40] border-[#243768] text-white placeholder:text-[#6C7EA6] text-center tracking-widest"
              data-testid="input-passcode"
            />
            <Button
              type="submit"
              className="w-full bg-cyan-500 hover:bg-cyan-400 text-[#060B1C] font-semibold"
              data-testid="btn-passcode-submit"
            >
              Unlock
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
